import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import customerInstructionsImgModal from "../../../customer-instructions.png";
import useCustomerInstructions from "@/hooks/useCustomerInstructions";

type Props = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerGroupId?: string;
}>;

export default function CustomerInstructionModal({
  isOpen,
  onClose,
  customerId,
  customerGroupId,
}: Props) {
  const { t } = useTranslation();
  const { 
    customerInstructions, 
    groupInstructions, 
    isLoading, 
    hasInstructions
  } = useCustomerInstructions(customerId, customerGroupId);
  
  useEffect(() => {
    if (!isLoading && !hasInstructions) {
      onClose();
    }
  }, [isLoading, hasInstructions, onClose]);
  if (!isOpen || isLoading) {
    return null;
  }
  
  return (
    <Modal
      isOpen={isOpen}
      backdrop="opaque"
      placement="top-center"
      size="xl"
      classNames={{
        body: "py-6",
        backdrop: "bg-[#292f46]/50 backdrop-opacity-40",
        base: "border-[#292f46] bg-white text-vistadark",
        header: "border-b-[1px] border-[#292f46] bg-vistagreen",
        footer: "border-t-[1px] border-[#e5e5e5]",
        closeButton: "hover:bg-vistagreen/50 active:bg-vistagreen/100",
      }}
    >
      <ModalContent>
        <ModalHeader>
        <div className="text-2xl font-bold pb-5 flex h-16 relative overflow-visible items-center justify-between w-full">
            <div className="text-right w-[60%] pt-6 leading-6">
                <p className="text-white font-light uppercase">{t("customers")}</p>
                <p className="text-vistadark font-black uppercase">{t("instructions")}</p>
            </div>
            <img
              src={customerInstructionsImgModal}
              alt={t("alt_vistaflow_instructions")}
              className="w-48 origin-left -top-4 absolute right-0"
            />
        </div>
        </ModalHeader>
        <ModalBody>
          {hasInstructions ? (
            <div className="mt-5">
              {customerInstructions?.customer_instruction && (
                <div className="bg-[#f9f9fb] m-1 p-2 rounded-md">
                  <div className="-mt-5">
                    <Chip
                      classNames={{
                        base: "bg-vistagreen text-white",
                        content: "drop-shadow shadow-black text-white",
                      }}
                    >
                      {t("customer_specific_instruction")}
                    </Chip>
                  </div>

                  <p className="text-sm p-2 whitespace-pre-line">
                    {customerInstructions.customer_instruction}
                  </p>
                </div>
              )}
              {customerInstructions?.customer_instruction &&
                groupInstructions?.customer_instruction && (
                  <div className="py-4">
                    <Divider />
                  </div>
                )}
              {groupInstructions?.customer_instruction && (
                <div className="bg-[#f9f9fb] m-1 p-2 rounded-md">
                  <div className="-mt-5">
                    <Chip
                      classNames={{
                        base: "bg-vistagreen text-white",
                        content: "drop-shadow shadow-black text-white",
                      }}
                    >
                      {t("customer_group_instruction")}
                    </Chip>
                  </div>

                  <p className="text-sm p-2 whitespace-pre-line">
                    {groupInstructions.customer_instruction}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p>{t("No instructions available")}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" variant="flat" onPress={onClose}>
            <span className="capitalize">{t("understand")}</span>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
